import { QueryClient, QueryKey } from '@tanstack/react-query';
import { logger } from './logger';

/**
 * Utility for focused query invalidation with additional safety checks
 * 
 * @param queryClient QueryClient instance to use for invalidation
 * @param queryKey Query key to invalidate
 * @param options Optional configuration for the invalidation
 * @returns Promise that resolves when invalidation is complete
 */
export async function invalidateQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
  options: {
    exact?: boolean;
    refetchType?: 'active' | 'inactive' | 'all';
    cancelRefetch?: boolean;
    context?: Record<string, unknown>;
  } = {}
): Promise<void> {
  try {
    const { exact = false, refetchType = 'active', cancelRefetch = false, context } = options;
    
    // Log invalidation for debugging
    logger.sync('Invalidating query', {
      queryKey,
      exact,
      refetchType,
      cancelRefetch,
      context
    });
    
    // Perform invalidation with options
    await queryClient.invalidateQueries({
      queryKey,
      exact,
      refetchType,
      cancelRefetch
    });
  } catch (error) {
    logger.error('Error during query invalidation:', error);
    // Don't throw so this doesn't break calling code
  }
}

/**
 * Utility for optimized query refetching with additional safety checks
 * 
 * @param queryClient QueryClient instance to use for refetching
 * @param queryKey Query key to refetch
 * @param options Optional configuration for the refetch
 * @returns Promise that resolves when refetch is complete
 */
export async function refetchQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
  options: {
    exact?: boolean;
    throwOnError?: boolean;
    cancelRefetch?: boolean;
    context?: Record<string, unknown>;
  } = {}
): Promise<unknown> {
  try {
    const { exact = false, throwOnError = false, cancelRefetch = true, context } = options;
    
    // Log refetch for debugging
    logger.sync('Refetching query', {
      queryKey,
      exact,
      throwOnError,
      cancelRefetch,
      context
    });
    
    // Perform refetch with options
    return await queryClient.refetchQueries({
      queryKey,
      exact,
      throwOnError,
      cancelRefetch
    });
  } catch (error) {
    logger.error('Error during query refetch:', error);
    
    if (options.throwOnError) {
      throw error;
    }
    
    return null;
  }
}

/**
 * Optimized batch invalidation for multiple query keys
 * 
 * @param queryClient QueryClient instance to use for invalidation
 * @param queryKeysMap Map of query keys to invalidate with optional config per key
 * @param globalOptions Optional global configuration that applies to all keys
 * @returns Promise that resolves when all invalidations are complete
 */
export async function batchInvalidateQueries(
  queryClient: QueryClient,
  queryKeysMap: Map<QueryKey, { 
    exact?: boolean; 
    refetchType?: 'active' | 'inactive' | 'all';
    cancelRefetch?: boolean;
  }>,
  globalOptions: {
    refetchType?: 'active' | 'inactive' | 'all';
    cancelRefetch?: boolean;
  } = {}
): Promise<void> {
  const invalidationPromises = Array.from(queryKeysMap.entries()).map(
    ([queryKey, options]) => {
      // Merge global options with key-specific options
      const mergedOptions = {
        ...globalOptions,
        ...options
      };
      
      return invalidateQuery(queryClient, queryKey, mergedOptions);
    }
  );
  
  try {
    await Promise.all(invalidationPromises);
  } catch (error) {
    logger.error('Error during batch query invalidation:', error);
  }
}

/**
 * Safely update query data without triggering unnecessary renders or invalidations
 * 
 * @param queryClient QueryClient instance to use for updating
 * @param queryKey Query key to update
 * @param updaterFn Function that takes old data and returns new data
 * @param options Optional configuration for the update
 * @returns Updated data if update was successful
 */
export function safeUpdateQueryData<TData = unknown>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  updaterFn: (oldData: TData | undefined) => TData | undefined,
  options: {
    exact?: boolean;
    context?: Record<string, unknown>;
  } = {}
): TData | undefined {
  try {
    const { exact = false, context } = options;
    
    // Get existing data to compare
    const oldData = queryClient.getQueryData<TData>(queryKey);
    
    // Apply updater
    const newData = updaterFn(oldData);
    
    // Skip update if data hasn't changed (prevents unnecessary re-renders)
    if (JSON.stringify(oldData) === JSON.stringify(newData)) {
      logger.sync('Skipping query update - data unchanged', {
        queryKey,
        context
      });
      return oldData;
    }
    
    // Log update for debugging
    logger.sync('Updating query data', {
      queryKey,
      exact,
      hasOldData: !!oldData,
      hasNewData: !!newData,
      context
    });
    
    // Set the updated data
    return queryClient.setQueryData<TData>(queryKey, newData);
  } catch (error) {
    logger.error('Error during query data update:', error);
    return undefined;
  }
}